import { Fragment } from 'react';
import { EyeOff } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';
import { entryByline } from '../../../lib/entries';
import {
  advancementCut,
  formatGap,
  formatPoints,
  isVoteCategory,
  leaderboardRows,
  stageStandings,
  withCommunityVotes,
} from '../../../lib/stages';

const RANK_STYLE = {
  1: 'text-gold border-l-gold',
  2: 'text-silver border-l-silver',
  3: 'text-bronze border-l-bronze font-semibold',
};

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const LeaderboardStageView = ({
  stage,
  playersById = {},
  nextStage,
  emptyText,
  votes = null,
  votesHidden = false,
}) => {
  const participants = stage.participants || [];
  if (participants.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border/60 px-6 py-12 text-center text-sm text-muted-foreground">
        {emptyText || 'Standings appear once this stage starts.'}
      </div>
    );
  }

  const categories = stage.categories || [];
  const showCategoryColumns = categories.length > 1;
  const showTotal = !votesHidden;
  const showGap = !votesHidden;

  const ranked = votes ? withCommunityVotes(stage, votes) : stage;
  const rows = leaderboardRows(ranked, votesHidden);

  let advancing = [];
  let tied = [];
  if (!votesHidden) {
    if (stage.status === 'completed' && nextStage) {
      advancing = nextStage.participants || [];
    } else if (nextStage?.advanceCount) {
      const cut = advancementCut(stageStandings(ranked), nextStage.advanceCount);
      advancing = cut.advancing;
      tied = cut.tied;
    }
  }
  const cutSet = new Set([...advancing, ...tied]);
  let lastCutIndex = -1;
  rows.forEach((r, i) => {
    if (cutSet.has(r.playerId)) lastCutIndex = i;
  });

  const totalHeader = categories.length === 1 ? 'Points' : 'Total';

  const columnCount =
    2 + (showCategoryColumns ? categories.length : 0) + (showTotal ? 1 : 0) + (showGap ? 1 : 0);

  return (
    <div className="space-y-2">
      {votesHidden && (
        <p className="text-xs text-muted-foreground">
          Standings appear when the community vote is revealed.
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">#</TableHead>
            <TableHead>{stage.categories?.some(isVoteCategory) ? 'Entry' : 'Player'}</TableHead>
            {showCategoryColumns &&
              categories.map((c) => (
                <TableHead key={c.id} className="text-right">
                  {c.name}
                </TableHead>
              ))}
            {showTotal && <TableHead className="text-right">{totalHeader}</TableHead>}
            {showGap && <TableHead className="text-right">Gap</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => {
            const player = playersById[row.playerId];
            const isAdvancing = !votesHidden && advancing.includes(row.playerId);
            const isTied = !votesHidden && tied.includes(row.playerId);
            const rankDisplay = votesHidden ? '—' : (row.rank ?? '—');
            const byline = entryByline(player);
            return (
              <Fragment key={row.playerId}>
                <TableRow className={cn((isAdvancing || isTied) && 'bg-heat/5')}>
                  <TableCell
                    className={cn(
                      'border-l-2 border-l-transparent text-right font-mono tabular-nums',
                      !votesHidden && RANK_STYLE[row.rank]
                    )}
                  >
                    {rankDisplay}
                  </TableCell>
                  <TableCell className="min-w-[10rem]">
                    <div className="flex min-w-0 items-center gap-2">
                      <Avatar className="h-7 w-7">
                        {player?.imageUrl && <AvatarImage src={player.imageUrl} alt="" />}
                        <AvatarFallback>{initials(player?.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="truncate">{player?.name || 'Removed player'}</div>
                        {byline && (
                          <div className="truncate text-xs text-muted-foreground">by {byline}</div>
                        )}
                      </div>
                      {player?.country && (
                        <img
                          src={`https://flagcdn.com/24x18/${player.country.toLowerCase()}.png`}
                          alt=""
                          className="h-[14px] w-[18px] shrink-0 rounded-[2px] object-cover"
                        />
                      )}
                      {isTied && (
                        <Badge variant="outline" className="border-warning/60 text-warning">
                          Tie
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {showCategoryColumns &&
                    categories.map((c) => {
                      if (isVoteCategory(c)) {
                        const v = votesHidden ? null : votes?.[row.playerId];
                        return (
                          <TableCell key={c.id} className="text-right font-mono tabular-nums">
                            {votesHidden ? (
                              <EyeOff
                                className="inline h-3.5 w-3.5 text-muted-foreground"
                                aria-label="Hidden"
                              />
                            ) : v?.count ? (
                              <>
                                {v.average.toFixed(1)}{' '}
                                <span className="text-muted-foreground">({v.count})</span>
                              </>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        );
                      }
                      const value = row.values[c.id];
                      return (
                        <TableCell key={c.id} className="text-right font-mono tabular-nums">
                          {value === null || value === undefined ? '—' : formatPoints(value)}
                        </TableCell>
                      );
                    })}
                  {showTotal && (
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.rank === null ? '—' : formatPoints(row.total)}
                    </TableCell>
                  )}
                  {showGap && (
                    <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                      {row.gap ? formatGap(row.gap) : ''}
                    </TableCell>
                  )}
                </TableRow>
                {i === lastCutIndex && i < rows.length - 1 && nextStage && (
                  <TableRow key={`${row.playerId}-cut`} className="hover:bg-transparent">
                    <TableCell colSpan={columnCount}>
                      <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-heat">
                        <span className="h-px flex-1 bg-heat/40" />
                        Top{' '}
                        {stage.status === 'completed'
                          ? advancing.length
                          : nextStage.advanceCount}{' '}
                        advance to {nextStage.name}
                        <span className="h-px flex-1 bg-heat/40" />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default LeaderboardStageView;
